import { createContext, useContext, useState } from "react";
import { login as loginService, validateSessionService } from "../../services/authService";
import LoadingOverlay from "../../components/Loading";
import { getErrorMessage } from '../../locales/translationUtils';

interface AuthContextProps {
  user: any;
  loginAction: (data: User) => Promise<void>;
  validateSession: () => Promise<boolean>;
  isAuthenticated: boolean;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

interface User {
  username: string;
  password: string;
  id: number;
  rememberMe: boolean;
}

const AuthContext = createContext<AuthContextProps | null>(null);

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  const loginAction = async (data: User) => {
    try {
      setLoading(true);
      const userData = await loginService(data);

      data.rememberMe
        ? localStorage.setItem('user', JSON.stringify(userData))
        : sessionStorage.setItem('user', JSON.stringify(userData));

      setUser({ ...data, id: userData.id });
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const validateSession = async () => {
    const userData = await validateSessionService();
  
    if (userData) {
       setUser(userData);
    }
  
    return !!userData; 
  };
  

  return (
    <AuthContext.Provider value={{ user, loginAction, validateSession, isAuthenticated: !!user }}>
      <LoadingOverlay isLoading={loading} />
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) { 
    throw new Error(getErrorMessage("wrongUseProviderError", 
      { 
        auth: useAuth?.name, 
        providerName: AuthProvider?.name
      }
    ));
  }
  return context;
};
